import { useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp,
  DollarSign, Loader2, MessageCircleWarning, Repeat2, ShieldAlert,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from 'react-i18next';
import { BRAIN_CLIENTS } from '../../../../shared/types/brain';

/**
 * Tab "Calidad" (admin) — resultado del eval continuo: juez LLM sobre cada
 * respuesta del Brain + señales implícitas (re-preguntas, sin citas, errores).
 */

// Nombres de clientes: del registro (shared/brain-clients/), no de una lista local.

function scoreColor(v: number | null): string {
  if (v === null) return 'text-gray-400';
  if (v >= 4) return 'text-emerald-600';
  if (v >= 3.3) return 'text-amber-600';
  return 'text-red-600';
}

function pct(v: number | null): string {
  return v === null ? '—' : `${Math.round(v * 100)}%`;
}

export function BrainQualityTab() {
  const { t } = useTranslation();
  const [openClient, setOpenClient] = useState<string | null>(null);
  // 02/10/2026: 5 min (era 60 s); el juez corre en cola, un minuto de delay no cambia la lectura.
  const { data, isLoading, error } = trpc.brain.evalStats.useQuery(undefined, {
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-red-600 py-6">{error.message}</p>;
  }
  if (!data || data.clients.length === 0) {
    return (
      <div className="py-10 text-center space-y-2">
        <CheckCircle2 className="w-8 h-8 text-gray-300 mx-auto" />
        <p className="text-sm text-gray-500">
          {t('brain.brainqualitytab.todavia_no_hay_evaluaciones_se_generan')}
        </p>
      </div>
    );
  }

  const totalCost = data.clients.reduce((a, c) => a + (c.judgeCostUsd ?? 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {t('brain.brainqualitytab.juez_llm_senales_implicitas_ultimos_30')}
        </p>
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          <DollarSign className="w-3 h-3" /> {t('brain.brainqualitytab.juez_costo', { v: totalCost.toFixed(2) })}
        </span>
      </div>

      {data.clients.map((c) => {
        const open = openClient === c.clientId;
        const flaggedCount = c.flagged.length;
        return (
          <div key={c.clientId} className="border rounded-xl overflow-hidden">
            <button
              onClick={() => setOpenClient(open ? null : c.clientId)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="font-semibold text-sm">
                  {BRAIN_CLIENTS[c.clientId] ?? c.clientId}
                </span>
                <span className="text-xs text-gray-400">{c.evaluated} evaluadas</span>
                {c.failedTurns > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 rounded-full px-2 py-0.5">
                    <ShieldAlert className="w-3 h-3" /> {c.failedTurns} {t('brain.brainqualitytab.respuestas_fallidas')}
                  </span>
                )}
                {flaggedCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">
                    <AlertTriangle className="w-3 h-3" /> {flaggedCount} flaggeadas
                  </span>
                )}
              </div>
              {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-gray-100 border-t">
              {[
                { label: 'Groundedness', value: c.avgGroundedness?.toFixed(1) ?? '—', cls: scoreColor(c.avgGroundedness) },
                { label: 'Relevancia', value: c.avgRelevance?.toFixed(1) ?? '—', cls: scoreColor(c.avgRelevance) },
                { label: t('brain.brainqualitytab.alucinacion'), value: pct(c.hallucinationRate), cls: (c.hallucinationRate ?? 0) > 0.1 ? 'text-red-600' : 'text-emerald-600' },
                { label: t('brain.brainqualitytab.sin_citas'), value: pct(c.noCitationsRate), cls: 'text-gray-700' },
                { label: 'Re-preguntas', value: pct(c.reAskRate), cls: (c.reAskRate ?? 0) > 0.2 ? 'text-amber-600' : 'text-gray-700' },
              ].map((kpi) => (
                <div key={kpi.label} className="bg-white px-3 py-2 text-center">
                  <p className={`text-lg font-bold ${kpi.cls}`}>{kpi.value}</p>
                  <p className="text-[10px] uppercase tracking-wide text-gray-400">{kpi.label}</p>
                </div>
              ))}
            </div>

            {open && (
              <div className="border-t divide-y">
                {flaggedCount === 0 ? (
                  <p className="px-4 py-3 text-xs text-gray-500">{t('brain.brainqualitytab.sin_respuestas_flaggeadas')}</p>
                ) : (
                  c.flagged.map((f) => (
                    <div key={f.evalId} className="px-4 py-3 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${
                          f.verdict === 'fail' ? 'bg-red-100 text-red-700' :
                          f.verdict === 'judge_error' ? 'bg-gray-100 text-gray-600' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {f.verdict === 'judge_error' ? t('brain.brainqualitytab.juez_s_verdicto') : f.verdict}
                        </span>
                        {f.hallucinated && (
                          <span className="text-[10px] text-red-600 font-medium">{t('brain.brainqualitytab.alucinacion_2')}</span>
                        )}
                        {f.reAsked && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700">
                            <Repeat2 className="w-3 h-3" /> re-preguntada
                          </span>
                        )}
                        {f.noCitations && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500">
                            <MessageCircleWarning className="w-3 h-3" /> {t('brain.brainqualitytab.sin_citas_2')}
                          </span>
                        )}
                        <span className="text-[10px] text-gray-400 ml-auto">
                          {String(f.createdAt).slice(0, 16).replace('T', ' ')} · {f.userEmail ?? '—'}
                        </span>
                      </div>
                      <p className="text-xs font-medium text-gray-800">{f.question}</p>
                      {f.answerPreview && (
                        <p className="text-xs text-gray-500 line-clamp-2">{f.answerPreview}</p>
                      )}
                      {f.reasoning && (
                        <p className="text-[11px] text-gray-400 italic">Juez: {f.reasoning}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
